// services/base.service.ts
import { api } from "@/lib/api";
import {
  ApiResponse,
  ApiError,
  PaginationParams,
} from "@/interfaces/api.interface";
import ToastService from "@/utils/toast";

export class BaseService {
  private baseUrl: string;

  constructor(private controllerName: string) {
    this.baseUrl = `${process.env.REACT_APP_BASE_URL}/api/${this.controllerName}`;
  }

  protected handleError(error: any): ApiError {
    if (error.response?.data) {
      return {
        message: error.response.data.message || "An error occurred",
        code: error.response.status.toString(),
        statusCode: error.response.status,
      };
    }
    return {
      message: error.message || "Network error occurred",
      code: "NETWORK_ERROR",
      statusCode: 500,
    };
  }

  protected buildQueryParams({
    pageNumber = 0,
    pageSize = 20,
    sort,
    filter,
    searchFilters = [],
  }: PaginationParams): URLSearchParams {
    let queryFilter = filter || {
      filters: [],
      logic: "and",
    };

    if (searchFilters?.length) {
      queryFilter.filters.push({
        logic: "or",
        filters: searchFilters,
      });
    }

    const paramsObj: Record<string, string> = {
      skip: pageNumber.toString(),
      take: pageSize.toString(),
      filter: JSON.stringify(queryFilter),
    };

    if (sort) {
      paramsObj.sort = JSON.stringify(sort);
    }

    return new URLSearchParams(paramsObj);
  }

  async getById<T>(id: string): Promise<ApiResponse<T>> {
    try {
      const res = await api.get(`${this.baseUrl}/${id}`);
      return { data: res.data as unknown as T, error: null };
    } catch (error) {
      return { data: null, error: this.handleError(error) };
    }
  }

  async get<T>(params: PaginationParams = {}): Promise<ApiResponse<T[]>> {
    try {
      const queryParams = this.buildQueryParams(params);
      const res: any = await api.get(`${this.baseUrl}?${queryParams}`);

      return {
        data: res.data.items,
        total: res.data.totalCount,
        error: null,
      };
    } catch (error) {
      return { data: null, error: this.handleError(error) };
    }
  }

  async getAll<T>(params: PaginationParams = {}, signal?: AbortSignal): Promise<ApiResponse<T[]>> {
    try {
      const queryParams = this.buildQueryParams(params);
      const res: any = await api.get(`${this.baseUrl}/all?${queryParams}`, {}, { signal });

      return {
        data: res.data,
        error: null,
      };
    } catch (error) {
      return { data: null, error: this.handleError(error) };
    }
  }

  async create<T>(payload: any): Promise<ApiResponse<T>> {
    const res: any = await api.post(this.baseUrl, payload);

    if (!res.ok && res?.status === 400) {
      ToastService.error(res.data.message);
      throw {
        data: res.data,
        status: res.status,
      };
    }

    if (!res.ok && res?.originalError?.message) {
      ToastService.error(res.data.message);
      throw {
        response: {
          data: res?.originalError?.message,
          status: 503,
        },
      };
    }
    if (res.data?.statusCode === 400 || res.data?.message) {
      ToastService.error(res.data.message);
      throw {
        response: {
          data: res.data,
          status: res.data.statusCode,
        },
      };
    }

    return {
      data: res.data as T,
      error: null,
    };
  }

  async update<T>(id: string, payload: any): Promise<ApiResponse<T>> {
    const res: any = await api.put(`${this.baseUrl}/${id}`, payload);

    if (res.data?.statusCode === 400 || res.data?.message) {
      ToastService.error(res.data.message);
      throw {
        response: {
          data: res.data,
          status: res.data.statusCode,
        },
      };
    }
    return { data: res.data as unknown as T, error: null };
  }
}
